import { FC, useMemo, useState } from "react";
import { Card, Button, Descriptions, InputNumber, Modal, Form, message } from "antd";
import { ArrowRight, DollarSign, Edit, ChevronDown, ChevronRight, User, FileText } from "lucide-react";
import { useEstimateDetailStore } from "../estimate-detail.store";
import { EstimateStatus, EstimateTicket, EstimateService } from "~/types/estimate";
import { ServiceType } from "~/utils/constants";
import { useGetStaffRoleByName } from "~/services/common";
import { estimateApi } from "~/services/estimate/estimate.api";
import { useNavigate } from "react-router-dom";

interface BillingTabProps {
  estimate?: EstimateTicket;
}

// Format currency helper
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined) return "$0.00";
  return `$${Number(amount ?? 0)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

// Interface for price overrides
interface PriceOverride {
  customTotal?: number;
}

export const BillingTab: FC<BillingTabProps> = ({ estimate }) => {
  const { setShowConvertModal } = useEstimateDetailStore();
  const navigate = useNavigate();
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
  const [priceOverrides, setPriceOverrides] = useState<Map<string, PriceOverride>>(new Map());
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState<EstimateService | null>(null);
  const [form] = Form.useForm();
  const [isConvertingToInvoice, setIsConvertingToInvoice] = useState(false);

  // Get Art Handler role for default rates
  const { data: artHandlerRole } = useGetStaffRoleByName("Art Handler", estimate?.clientId);

  if (!estimate) return null;

  // Get client name - tempClientData takes priority over linked client
  const getClientName = () => {
    if (estimate.tempClientData) {
      return estimate.tempClientData.name;
    }
    if (estimate.client) {
      return estimate.client.companyName ||
             `${estimate.client.firstName} ${estimate.client.lastName}`.trim() ||
             "Unnamed Client";
    }
    return "No Client";
  };

  const clientName = getClientName();

  const canConvert = estimate.status !== EstimateStatus.CONVERTED;

  // Handle convert to invoice
  const handleConvertToInvoice = async () => {
    if (!estimate.clientId && !estimate.tempClientData) {
      message.error('Estimate must be linked to a client to convert to invoice');
      return;
    }

    setIsConvertingToInvoice(true);
    try {
      const invoice = await estimateApi.convertToInvoice(estimate.id);
      message.success(`Estimate converted to invoice ${invoice.invoiceNumber}`);
      // Navigate to invoice
      navigate(`/billing/invoicing/${invoice.id}`);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to convert estimate to invoice');
    } finally {
      setIsConvertingToInvoice(false);
    }
  };

  // Toggle service expansion
  const toggleExpand = (serviceId: string) => {
    const newExpanded = new Set(expandedServices);
    if (newExpanded.has(serviceId)) {
      newExpanded.delete(serviceId);
    } else {
      newExpanded.add(serviceId);
    }
    setExpandedServices(newExpanded);
  };

  // Open price override modal
  const openOverrideModal = (service: EstimateService) => {
    setCurrentService(service);
    const override = priceOverrides.get(service.id);
    form.setFieldsValue({
      customTotal: override?.customTotal ?? calculateServiceCost(service).total,
    });
    setOverrideModalOpen(true);
  };

  // Save price override
  const handleSaveOverride = () => {
    const values = form.getFieldsValue();
    if (currentService) {
      const newOverrides = new Map(priceOverrides);
      newOverrides.set(currentService.id, {
        customTotal: values.customTotal,
      });
      setPriceOverrides(newOverrides);
      setOverrideModalOpen(false);
      setCurrentService(null);
      message.success('Price override saved');
    }
  };

  // Calculate service cost based on service type
  const calculateServiceCost = (service: EstimateService) => {
    const override = priceOverrides.get(service.id);

    // If there's a custom override, use it
    if (override?.customTotal !== undefined) {
      return {
        total: override.customTotal,
        breakdown: { custom: override.customTotal },
      };
    }

    // If service has an estimated price set manually, use it
    if (service.estimatedPrice) {
      return {
        total: service.estimatedPrice,
        breakdown: { estimated: service.estimatedPrice },
      };
    }

    const durationHours = Number(service.duration) || 1;
    const staffCount = Number(service.staffCount) || 1;
    const staffHourlyRate = Number(artHandlerRole?.hourlyRate) || 100;
    const vesselHourlyRate = Number(service.vessel?.hourlyRate) || 0; // Use actual vessel rate

    console.log('🔢 Calculating cost for service:', {
      serviceType: service.serviceType,
      durationHours,
      staffCount,
      staffHourlyRate,
      vesselHourlyRate,
      vessel: service.vessel,
    });

    let total = 0;
    let staffCost = 0;
    let vesselCost = 0;
    let volumeCost = 0;

    // Calculate based on service type
    let actualStaffCount = staffCount;
    let actualHours = durationHours;

    if (
      service.serviceType === ServiceType.COLLECTION ||
      service.serviceType === ServiceType.DELIVERY
    ) {
      // Collection/Delivery: (Staff + Vehicle) × Hours
      // Assume 30 min travel time for estimates
      const travelHours = 0.5;
      const totalHours = travelHours + durationHours;
      const estimatedStaff = service.staffCount || 2; // Default 2 staff for transport

      actualStaffCount = estimatedStaff;
      actualHours = totalHours;

      staffCost = estimatedStaff * staffHourlyRate * totalHours;

      if (service.vessel) {
        vesselCost = vesselHourlyRate * totalHours;
      }

      total = staffCost + vesselCost;

    } else if (service.serviceType === ServiceType.ON_SITE_SERVICE) {
      // Check if this is an expo service (has expo days with positions)
      if (service.expoDays && service.expoDays.length > 0) {
        // Expo Mode: Calculate from position assignments (straight time, no minimums)
        let expoTotal = 0;
        service.expoDays.forEach((day) => {
          if (day.positions) {
            day.positions.forEach((pos) => {
              const rate = Number(pos.hourlyRate) || 0;
              const posSubtotal = pos.quantity * pos.duration * rate;
              expoTotal += posSubtotal;
            });
          }
        });
        total = expoTotal;
        staffCost = expoTotal; // For breakdown display
      } else {
        // Regular On-Site Service: Staff Only × Site Time with $300 minimum per staff
        const estimatedStaff = service.staffCount || 1;
        actualStaffCount = estimatedStaff;

        staffCost = estimatedStaff * staffHourlyRate * durationHours;

        // $300 minimum per staff member
        const minimumCharge = 300 * estimatedStaff;
        total = Math.max(staffCost, minimumCharge);
      }

    } else if (service.serviceType === ServiceType.WAREHOUSE_SERVICE) {
      // Warehouse: Volume-based pricing ($1/ft³ with $75 minimum)
      // Estimate 5 cubic feet per item if item count provided
      const estimatedCubicFeet = service.estimatedItemCount ? service.estimatedItemCount * 5 : 60;

      const warehouseRate = 1; // $1 per cubic foot
      const minimumCharge = 75; // $75 minimum
      volumeCost = Math.max(estimatedCubicFeet * warehouseRate, minimumCharge);
      total = volumeCost;

    } else if (service.serviceType === ServiceType.FABRICATION_SERVICE) {
      // Fabrication: 1.5x hourly rate for specialized work
      const estimatedStaff = service.staffCount || 1;
      actualStaffCount = estimatedStaff;

      staffCost = estimatedStaff * (staffHourlyRate * 1.5) * durationHours;
      total = staffCost;

    } else {
      // Fallback for other service types
      actualStaffCount = service.staffCount || 1;
      staffCost = actualStaffCount * staffHourlyRate * durationHours;
      total = staffCost;
    }

    const result = {
      total,
      breakdown: {
        staffCost,
        vesselCost,
        volumeCost,
        durationHours: actualHours,
        staffCount: actualStaffCount,
        staffHourlyRate,
        vesselHourlyRate,
      },
    };

    console.log('💰 Cost calculation result:', result);

    return result;
  };

  // Calculate totals
  const estimateTotals = useMemo(() => {
    if (!estimate.services || estimate.services.length === 0) {
      return { subtotal: 0, total: 0 };
    }

    const subtotal = estimate.services.reduce((sum, service) => {
      return sum + calculateServiceCost(service).total;
    }, 0);

    return {
      subtotal,
      total: subtotal,
    };
  }, [estimate.services, priceOverrides, artHandlerRole]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Billing & Pricing</h2>
        <div className="text-right">
          <div className="text-sm text-gray-500">Estimated Total</div>
          <div className="text-2xl font-bold text-blue-600">
            {formatCurrency(estimateTotals.total)}
          </div>
        </div>
      </div>

      {/* Pricing Information */}
      <Card title="Services Breakdown" className="shadow-sm">
        <div className="space-y-4">
          {estimate.services && estimate.services.length > 0 ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                <div className="text-sm text-blue-800">
                  <strong>Pricing Rules:</strong>
                  <ul className="list-disc ml-5 mt-2 space-y-1">
                    <li><strong>Collection/Delivery:</strong> (Staff + Vehicle) × Hours (includes 30min travel estimate)</li>
                    <li><strong>On-Site Service:</strong> Staff × Hours ($300 minimum per staff)</li>
                    <li><strong>Expo Mode (On-Site):</strong> Position × Qty × Hours (straight time, no minimums)</li>
                    <li><strong>Warehouse:</strong> $1/ft³ based on item count (~5 ft³/item, $75 minimum)</li>
                    <li><strong>Fabrication:</strong> 1.5× staff rate for specialized work</li>
                  </ul>
                </div>
              </div>

              {estimate.services.map((service) => {
                const cost = calculateServiceCost(service);
                const isExpanded = expandedServices.has(service.id);
                const override = priceOverrides.get(service.id);

                return (
                  <Card key={service.id} size="small" className="shadow-sm">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <button
                            onClick={() => toggleExpand(service.id)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </button>
                          <h4 className="font-medium capitalize">
                            {service.serviceType.toLowerCase().replace(/_/g, ' ')}
                          </h4>
                          {override?.customTotal && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              Custom Price
                            </span>
                          )}
                          {service.expoDays && service.expoDays.length > 0 && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                              Expo Mode ({service.expoDays.length} days)
                            </span>
                          )}
                        </div>

                        <div className="ml-7 text-sm text-gray-600 space-y-1">
                          <div className="flex items-center space-x-1">
                            <User size={14} className="text-gray-400" />
                            <span className="font-medium">{clientName}</span>
                            {estimate.tempClientData && (
                              <span className="text-xs text-orange-600">(Quick Contact)</span>
                            )}
                          </div>
                          {service.duration && <div>Duration: {service.duration}h</div>}
                          {service.staffCount && <div>Staff: {service.staffCount}</div>}
                          {service.vessel && <div>Vessel: {service.vessel.name}{service.vessel.hourlyRate ? ` ($${service.vessel.hourlyRate}/hr)` : ''}</div>}
                          {service.estimatedItemCount && <div>Items: ~{service.estimatedItemCount}</div>}
                        </div>

                        {isExpanded && cost.breakdown && !override?.customTotal && (
                          <div className="ml-7 mt-3 p-3 bg-gray-50 rounded text-sm space-y-1">
                            <div className="font-medium mb-2">Cost Breakdown:</div>
                            {(cost.breakdown.staffCost ?? 0) > 0 && (
                              <div className="flex justify-between">
                                <span>Staff Cost ({cost.breakdown.staffCount} × ${cost.breakdown.staffHourlyRate}/hr × {cost.breakdown.durationHours?.toFixed(1)}h):</span>
                                <span>{formatCurrency(cost.breakdown.staffCost)}</span>
                              </div>
                            )}
                            {(cost.breakdown.vesselCost ?? 0) > 0 && (
                              <div className="flex justify-between">
                                <span>Vessel Cost (${cost.breakdown.vesselHourlyRate}/hr × {cost.breakdown.durationHours?.toFixed(1)}h):</span>
                                <span>{formatCurrency(cost.breakdown.vesselCost)}</span>
                              </div>
                            )}
                            {(cost.breakdown.volumeCost ?? 0) > 0 && (
                              <div className="flex justify-between">
                                <span>Volume-based pricing:</span>
                                <span>{formatCurrency(cost.breakdown.volumeCost)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-3">
                        <Button
                          size="small"
                          icon={<Edit size={14} />}
                          onClick={() => openOverrideModal(service)}
                        >
                          Edit Price
                        </Button>
                        <div className="text-right">
                          <div className="font-semibold text-lg">
                            {formatCurrency(cost.total)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}

              {/* Grand Total */}
              <div className="border-t-2 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <div className="text-lg font-semibold">Estimated Total</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {formatCurrency(estimateTotals.total)}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <DollarSign size={48} className="mx-auto mb-3 text-gray-300" />
              <p>No services added yet</p>
              <p className="text-sm">Add services to create your estimate</p>
            </div>
          )}
        </div>
      </Card>

      {/* Convert to Invoice */}
      {canConvert && (
        <Card className="shadow-sm bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 mb-4">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="bg-green-100 p-3 rounded-full">
                <FileText className="text-green-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 mb-2">Convert to Invoice</h3>
                <p className="text-sm text-green-700 mb-4">
                  Ready to bill? Convert this estimate directly to an invoice.
                  Reference will change from <strong>{estimate.referenceNumber}</strong> to <strong>INV{estimate.referenceNumber.replace(/^EST-?/i, '')}</strong>
                </p>
                <p className="text-sm text-green-700 mb-4">
                  Estimated total: <strong>{formatCurrency(estimateTotals.total)}</strong>
                </p>
                <ul className="text-sm text-green-700 space-y-1 mb-4">
                  <li>• Creates invoice with same reference number (EST → INV)</li>
                  <li>• Ready to send to client</li>
                  <li>• Can sync to QuickBooks</li>
                </ul>
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<FileText size={16} />}
              onClick={handleConvertToInvoice}
              loading={isConvertingToInvoice}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Convert to Invoice
            </Button>
          </div>
        </Card>
      )}

      {/* Convert to Booking */}
      {canConvert && (
        <Card className="shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <ArrowRight className="text-blue-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">Convert to Full Booking</h3>
                <p className="text-sm text-blue-700 mb-4">
                  Ready to move forward? Convert this estimate into a scheduled booking.
                  Estimated total: <strong>{formatCurrency(estimateTotals.total)}</strong>
                </p>
                <ul className="text-sm text-blue-700 space-y-1 mb-4">
                  <li>• Assign to a project</li>
                  <li>• Schedule specific service times</li>
                  <li>• Assign staff and vehicles</li>
                  <li>• Generate invoices</li>
                </ul>
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<ArrowRight size={16} />}
              onClick={() => setShowConvertModal(true)}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Convert to Booking
            </Button>
          </div>
        </Card>
      )}

      {/* Converted Status */}
      {estimate.status === EstimateStatus.CONVERTED && estimate.convertedToJobTicketId && (
        <Card className="shadow-sm bg-purple-50 border-purple-200">
          <div className="text-center py-6">
            <div className="text-purple-600 mb-3">
              <ArrowRight size={48} className="mx-auto" />
            </div>
            <h3 className="font-semibold text-purple-900 mb-2">Converted to Booking</h3>
            <p className="text-sm text-purple-700 mb-4">
              This estimate has been converted into a full booking
            </p>
            <Button
              type="primary"
              href={`/bookings/${estimate.convertedToJobTicketId}`}
              className="bg-purple-600"
            >
              View Booking
            </Button>
          </div>
        </Card>
      )}

      {/* Price Override Modal */}
      <Modal
        title="Edit Service Price"
        open={overrideModalOpen}
        onOk={handleSaveOverride}
        onCancel={() => {
          setOverrideModalOpen(false);
          setCurrentService(null);
        }}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Custom Total Price"
            name="customTotal"
            help="Override the calculated price with a custom amount"
          >
            <InputNumber
              prefix="$"
              min={0}
              step={10}
              style={{ width: '100%' }}
              placeholder="Enter custom price"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
